import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { auth } from "../lib/firebase";
import { api } from "../lib/api";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

/** Same floor as ChangePasswordDialog: these accounts hold a hall's ledger. */
const MIN_PASSWORD = 8;

/**
 * Firebase auth codes → a sentence the person can act on. The raw Firebase
 * message ("Firebase: Error (auth/invalid-credential).") is English developer
 * text and must never reach a hall owner.
 */
function authErrorKey(error: unknown): StringKey {
  const code = error instanceof FirebaseError ? error.code : "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "auth_err_invalid_credential";
    case "auth/email-already-in-use":
      return "auth_err_email_in_use";
    case "auth/invalid-email":
      return "auth_err_invalid_email";
    case "auth/weak-password":
      return "auth_err_weak";
    case "auth/too-many-requests":
      return "cp_err_too_many";
    case "auth/network-request-failed":
      return "cp_err_network";
    case "auth/user-disabled":
      return "auth_err_disabled";
    default:
      return "auth_err_generic";
  }
}

/**
 * Owners, managers and admins sign in here — and a hall that is not on Talar
 * yet creates its account here. A new account has no organization, so after
 * sign-up RequireRole routes it to OnboardingPage, which names the hall's
 * organization and mints the owner role. (Customers use the Android app.)
 */
export function LoginPage() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const t = useT();
  const signup = mode === "signup";

  function switchMode(next: "signin" | "signup") {
    setMode(next);
    setError(null);
    setNotice(null);
    setPassword("");
    setRepeat("");
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (signup) {
      if (password.length < MIN_PASSWORD) {
        setError(t("signup_password_short", { n: MIN_PASSWORD }));
        return;
      }
      if (password !== repeat) {
        setError(t("signup_password_mismatch"));
        return;
      }
    }
    setBusy(true);
    try {
      if (signup) {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (name.trim()) await updateProfile(cred.user, { displayName: name.trim() });
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
      // Creates the profile and the default role claim; idempotent.
      await api("/users/bootstrap", { method: "POST" });
      await auth.currentUser?.getIdToken(true);
      navigate("/");
    } catch (err) {
      setError(t(authErrorKey(err)));
    } finally {
      setBusy(false);
    }
  }

  async function forgotPassword() {
    setError(null);
    setNotice(null);
    if (!email.trim()) {
      setError(t("forgot_need_email"));
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setNotice(t("forgot_sent"));
    } catch (err) {
      // Same answer whether or not the account exists — never confirm to a
      // stranger which emails are registered.
      const code = err instanceof FirebaseError ? err.code : "";
      if (code === "auth/user-not-found") setNotice(t("forgot_sent"));
      else setError(t(authErrorKey(err)));
    }
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{
        p: 2,
        background:
          "radial-gradient(1100px 500px at 100% -10%, rgba(14,107,92,0.10), transparent 60%)," +
          "radial-gradient(900px 500px at -10% 110%, rgba(184,134,11,0.10), transparent 55%)," +
          "#F7F6F2",
      }}
    >
      <Card sx={{ width: 440, maxWidth: "100%", p: 1, boxShadow: "0 10px 40px rgba(29,28,24,0.08)" }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: -1 }}>
            <LanguageSwitcher edge="end" />
          </Box>
          <Stack alignItems="center" spacing={0.5} mb={3}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: "rgba(14,107,92,0.10)",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <Typography variant="h4" fontWeight={800} lineHeight={1}>
                {t("app_initial")}
              </Typography>
            </Box>
            <Typography variant="h4" color="primary.main" fontWeight={800}>
              {t("app_name")}
            </Typography>
            <Typography color="text.secondary" variant="body2" textAlign="center">
              {signup ? t("signup_title") : t("login_subtitle")}
            </Typography>
          </Stack>

          {signup && (
            <Alert severity="info" icon={false} sx={{ mb: 1 }}>
              {t("signup_subtitle")}
            </Alert>
          )}

          <form onSubmit={submit} noValidate>
            {signup && (
              <TextField
                label={t("signup_name")}
                fullWidth
                margin="normal"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                autoFocus
              />
            )}
            <TextField
              label={t("login_email")}
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus={!signup}
              required
              inputProps={{ dir: "ltr" }}
            />
            <TextField
              label={t("login_password")}
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={signup ? "new-password" : "current-password"}
              helperText={signup ? t("cp_min_helper", { n: MIN_PASSWORD }) : undefined}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t("cp_hide_password") : t("cp_show_password")}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {signup && (
              <TextField
                label={t("signup_password_repeat")}
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
                error={repeat.length > 0 && repeat !== password}
                required
              />
            )}

            {!signup && (
              <Box sx={{ textAlign: "end", mt: 0.5 }}>
                <Link component="button" type="button" variant="body2" onClick={forgotPassword}>
                  {t("login_forgot")}
                </Link>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            )}
            {notice && (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                {notice}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || !email.trim() || !password}
              sx={{ mt: 3, py: 1.2 }}
            >
              {signup
                ? busy
                  ? t("signup_submitting")
                  : t("signup_submit")
                : busy
                  ? t("login_submitting")
                  : t("login_submit")}
            </Button>
          </form>

          <Divider sx={{ my: 3 }} />
          {signup ? (
            <Box textAlign="center">
              <Link component="button" type="button" variant="body2" onClick={() => switchMode("signin")}>
                {t("login_have_account")}
              </Link>
            </Box>
          ) : (
            <Stack alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t("login_no_account")}
              </Typography>
              <Button variant="outlined" fullWidth onClick={() => switchMode("signup")}>
                {t("login_create_account")}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
